import React from 'react';
import { App, Flex, Tour, type TourProps } from 'antd';
import { AggregatedMetrics } from '@prerender/prerender-shared/FE';
import type { FilterSelection } from '@prerender/design-system';
import { CrawlerPicker, type CrawlerPickerChange } from '@prerender/design-system/CrawlerPicker';

import {
  type CrawlerFilter,
  crawlerTaxonomyFor,
  fromPicker,
  pickerCategories,
  toCrawlerPickedEvent,
  toCategories,
  toPicker,
} from '~features/crawler-filter';
import { useCrawlerCategoryScheme } from '~features/crawler-filter/CrawlerFilterHooks';
import { useGetCrawlersFiltersListQuery } from '~features/crawler-filter/CrawlerFilterApi';
import { DomainFilter } from '~features/domain-manager/filter';
import { useGetPagesCrawledQuery } from '~features/dashboard-v3/DashboardV3API';
import { DASHBOARD_DEFAULT_PERIOD } from './defaultDateRange';
import { toDatePickerTracker } from './datePickerEvents';
import PlanGatedDateRangePicker from './PlanGatedDateRangePicker';
import MoreActionsDropdown from './MoreActionsDropdown';
import type { MoreActionsExportConfig } from './MoreActionsDropdown';
import type { FiltersEventMap } from './FiltersEvents';
import { FeatureReleaseFlags, useFeatureReleaseVisibility, FEATURE_RELEASE_TOUR_Z_INDEX } from '~features/FeatureReleases';
import { UpgradePlanProvider } from '~features/UpgradePlan';
import { openExportWindow } from '~library/print';

type TrackFunction = <E extends keyof FiltersEventMap>(event: E, props: FiltersEventMap[E]) => void;

type ExportConfig = Omit<MoreActionsExportConfig, 'onExportAsPdf'> & {
  url: string;
  /**
   * Builds the query string appended to `url` when opening the export tab.
   * Called at click time — needed for state that doesn't re-render the page,
   * like chart legend visibility read from the live Chart.js instances.
   */
  search?: () => string;
};

export type ChartDescriptionsConfig = {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
};

export type FiltersUpdate = Partial<Omit<AggregatedMetrics.BaseGraphQueryParams, 'crawlers'>> & {
  crawlerFilter?: CrawlerFilter;
};

type FiltersProps = {
  updateFilters: (newFilters: FiltersUpdate) => void;
  domainSelected?: string;
  crawlerFilter: CrawlerFilter;
  dateFrom: Date;
  dateTo: Date;
  trackEvent: TrackFunction;
  export: ExportConfig;
  chartDescriptions: ChartDescriptionsConfig;
};

function Filters({
  updateFilters,
  domainSelected,
  crawlerFilter,
  dateFrom,
  dateTo,
  trackEvent,
  export: exportConfig,
  chartDescriptions,
}: FiltersProps) {
  const { message } = App.useApp();
  const [shouldShowMoreActionsTour, markMoreActionsTourSeen] = useFeatureReleaseVisibility(
    FeatureReleaseFlags.DashboardPdfExportTourSeen
  );
  const [moreActionsDropdownTarget, setMoreActionsDropdownTarget] = React.useState<HTMLDivElement | null>(null);
  const categoryScheme = useCrawlerCategoryScheme();
  const AllCrawlersResult = useGetCrawlersFiltersListQuery({ categoryScheme });
  const CrawlersPerDomainResult = useGetCrawlersFiltersListQuery({ domain: domainSelected, categoryScheme });
  const PagesCrawledResult = useGetPagesCrawledQuery({
    domain: domainSelected,
    dateFrom,
    dateTo,
    crawlers: [],
    categoryScheme,
  });
  const crawlersWithData = React.useMemo(
    () => PagesCrawledResult.data?.data.map((item) => item.crawler),
    [PagesCrawledResult.data]
  );

  const allCrawlers = React.useMemo(() => AllCrawlersResult.data || [], [AllCrawlersResult.data]);
  const availableCrawlers = React.useMemo(() => CrawlersPerDomainResult.data || [], [CrawlersPerDomainResult.data]);
  const crawlerListsLoading = AllCrawlersResult.isLoading || CrawlersPerDomainResult.isLoading;
  const allCrawlersReady = AllCrawlersResult.isSuccess;
  const pickerCrawlers = React.useMemo(
    () => pickerCategories(allCrawlers, categoryScheme),
    [allCrawlers, categoryScheme]
  );
  const unavailableForDomain = React.useMemo(
    () =>
      CrawlersPerDomainResult.isSuccess ? pickerCrawlers.filter((crawler) => !availableCrawlers.includes(crawler)) : [],
    [CrawlersPerDomainResult.isSuccess, pickerCrawlers, availableCrawlers]
  );
  const unavailableForTimeRange = React.useMemo(
    () => (crawlersWithData ? pickerCrawlers.filter((crawler) => !crawlersWithData.includes(crawler)) : []),
    [pickerCrawlers, crawlersWithData]
  );
  const pickerValue =
    !allCrawlersReady && crawlerFilter.kind === 'some'
      ? toCategories(crawlerFilter, categoryScheme)
      : toPicker(crawlerFilter, pickerCrawlers, categoryScheme);

  const onDomainSelected = (domain?: string) => {
    trackEvent('DomainSelected', { selected_domain: domain ?? 'All domains' });
    updateFilters({ domain });
  };

  const onCrawlersPicked = (selection: FilterSelection, change: CrawlerPickerChange) => {
    trackEvent('CrawlerSelected', toCrawlerPickedEvent(selection, pickerCrawlers, change, categoryScheme));
    updateFilters({ crawlerFilter: fromPicker(selection, pickerCrawlers, categoryScheme) });
  };

  const onCrawlerPickerOpenChange = (open: boolean) => {
    if (open) trackEvent('CrawlerPickerOpened', {});
  };

  const onDatesSelected = (from: Date, to: Date) => {
    const amountOfDays = Math.floor((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    trackEvent('TimeframeSelected', { time_selected: amountOfDays });
    updateFilters({ dateFrom: from, dateTo: to });
  };

  const onDatePickerEvent = React.useMemo(
    () => toDatePickerTracker((...[event, properties]) => trackEvent(event, properties)),
    [trackEvent]
  );

  const onExportAsPdf = () => {
    if (exportConfig.loading || exportConfig.disabled) return;

    const exportWindow = openExportWindow(`${exportConfig.url}${exportConfig.search?.() ?? ''}`);

    if (!exportWindow) {
      message.error('Could not open export tab. Check your browser settings and try again.');
      return;
    }

    trackEvent('ExportAsPdf', {});
  };

  const onChartDescriptionsChange = (enabled: boolean) => {
    chartDescriptions.onChange(enabled);
    trackEvent('ChartDescriptionsToggled', { action: enabled ? 'enabled' : 'disabled' });
  };

  const moreActionsTourSteps = React.useMemo<TourProps['steps']>(
    () => [
      {
        title: 'More actions',
        description: 'Click here to export your dashboard to PDF',
        target: moreActionsDropdownTarget,
        placement: 'bottom',
        nextButtonProps: {
          children: 'Got it',
        },
      },
    ],
    [moreActionsDropdownTarget]
  );

  return (
    <UpgradePlanProvider>
      <Tour
        open={shouldShowMoreActionsTour && Boolean(moreActionsDropdownTarget)}
        onClose={markMoreActionsTourSeen}
        steps={moreActionsTourSteps}
        zIndex={FEATURE_RELEASE_TOUR_Z_INDEX}
      />
      <Flex gap="small" wrap align="center">
        <DomainFilter domain={domainSelected} onDomainChange={onDomainSelected} />

        <CrawlerPicker
          taxonomy={crawlerTaxonomyFor(categoryScheme)}
          categories={pickerCrawlers}
          loading={crawlerListsLoading}
          value={pickerValue}
          onChange={onCrawlersPicked}
          onOpenChange={onCrawlerPickerOpenChange}
          unavailableForDomain={unavailableForDomain}
          unavailableForTimeRange={unavailableForTimeRange}
          domainLabel={domainSelected}
        />

        <PlanGatedDateRangePicker
          disabled={crawlerListsLoading}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onDatesSelected}
          defaultPeriod={DASHBOARD_DEFAULT_PERIOD}
          trackEvent={onDatePickerEvent}
          placement="bottomRight"
        />
        <div ref={setMoreActionsDropdownTarget}>
          <MoreActionsDropdown
            export={{
              label: exportConfig.label,
              disabled: Boolean(exportConfig.disabled),
              loading: Boolean(exportConfig.loading),
              onExportAsPdf,
            }}
            chartDescriptions={{
              enabled: chartDescriptions.enabled,
              onChange: onChartDescriptionsChange,
            }}
          />
        </div>
      </Flex>
    </UpgradePlanProvider>
  );
}

export default Filters;
